
import { createContext, useContext } from 'react';

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string;
  avatar: string;
  coverImage: string;
  bio: string;
  isAdmin: boolean;
  isSubscribed: boolean;
  isVerified?: boolean;
  subscriptionTier: string | null;
  subscriptionExpiry: Date | null;
  subscribers: number;
  posts: number;
  likes: number;
  earnings: number;
  availableBalance: number;
  pendingBalance: number;
  location?: string;
  website?: string;
  joinDate: Date;
  online: boolean;
  paypalEmail?: string;
  shippingAddresses: ShippingAddress[];
  wishlist: string[];
  notificationPrefs: NotificationPrefs;
}

export interface ShippingAddress {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  isDefault: boolean;
}

export interface NotificationPrefs {
  newPosts: boolean;
  messages: boolean;
  orderUpdates: boolean;
  newProducts: boolean;
  promotions: boolean;
}

export interface Post {
  id: string;
  content: string;
  mediaType: 'image' | 'video' | 'text';
  mediaUrl?: string;
  mediaUrls?: string[];
  isLocked: boolean;
  price?: number;
  likes: number;
  comments: number;
  tips: number;
  likedBy: string[];
  savedBy: string[];
  timestamp: Date;
  scheduledFor?: Date;
  isStory?: boolean;
  expiresAt?: Date;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice?: number;
  images: string[];
  category: string;
  sizes: ProductSize[];
  stock: number;
  featured: boolean;
  createdAt: Date;
  sold: number;
  rating: number;
  reviews: number;
}

export interface ProductSize {
  size: string;
  stock: number;
}

export interface CartItem {
  productId: string;
  size: string;
  quantity: number;
  addedAt: Date;
}

export interface Order {
  id: string;
  userId: string;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: ShippingAddress;
  trackingNumber?: string;
  paypalTransactionId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface OrderItem {
  productId: string;
  productName: string;
  productImage: string;
  size: string;
  quantity: number;
  price: number;
}

export interface Message {
  id: string;
  fromUserId: string;
  toUserId: string;
  content: string;
  mediaUrl?: string;
  timestamp: Date;
  read: boolean;
  price?: number;
  isPaid?: boolean;
}

export interface Notification {
  id: string;
  type: 'like' | 'comment' | 'subscribe' | 'tip' | 'message' | 'order' | 'product' | 'post';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  link?: string;
}

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  features: string[];
  isPopular?: boolean;
}

export interface Analytics {
  totalRevenue: number;
  subscriptionRevenue: number;
  productRevenue: number;
  tipRevenue: number;
  totalSubscribers: number;
  activeSubscribers: number;
  totalOrders: number;
  pendingOrders: number;
  totalProducts: number;
  lowStockProducts: number;
  revenueByMonth: { month: string; amount: number }[];
  subscribersByMonth: { month: string; count: number }[];
  topProducts: { name: string; sales: number }[];
}

interface AppContextType {
  currentUser: User;
  adminUser: User;
  posts: Post[];
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  messages: Message[];
  notifications: Notification[];
  subscriptionTiers: SubscriptionTier[];
  analytics: Analytics;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  viewMode: 'dark' | 'light';
  setViewMode: (mode: 'dark' | 'light') => void;
  likePost: (postId: string) => void;
  savePost: (postId: string) => void;
  tipCreator: (amount: number) => void;
  sendMessage: (content: string, price?: number) => void;
  addToCart: (productId: string, size: string, quantity: number) => void;
  removeFromCart: (productId: string, size: string) => void;
  updateCartQuantity: (productId: string, size: string, quantity: number) => void;
  clearCart: () => void;
  toggleWishlist: (productId: string) => void;
  placeOrder: (shippingAddressId: string) => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => void;
  subscribe: (tierId: string) => void;
  unlockContent: (postId: string) => void;
  addProduct: (product: Omit<Product, 'id' | 'createdAt' | 'sold' | 'rating' | 'reviews'>) => void;
  updateProduct: (productId: string, updates: Partial<Product>) => void;
  deleteProduct: (productId: string) => void;
  createPost: (post: Omit<Post, 'id' | 'likes' | 'comments' | 'tips' | 'likedBy' | 'savedBy' | 'timestamp'>) => void;
  deletePost: (postId: string) => void;
  updateProfile: (updates: Partial<User>) => void;
  addShippingAddress: (address: Omit<ShippingAddress, 'id'>) => void;
  requestPayout: (amount: number) => void;
  markNotificationRead: (notificationId: string) => void;
  cartTotal: number;
  cartCount: number;
  isAdminView?: boolean;
}

export const AppContext = createContext<AppContextType | null>(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppContext');
  return context;
};
